import { useState, useRef, useCallback } from 'react';

/**
 * useKokoro — text-to-speech via local Kokoro TTS server (tunneled).
 * Drop-in replacement for useElevenLabs.
 *
 * Splits text into sentences and synthesizes each one individually so Kokoro
 * produces natural phrasing per sentence instead of breaking words across a
 * single long chunk.
 */
const KOKORO_URL = 'https://tts.buildabetterweb.site';

/**
 * Preprocess text for better TTS pronunciation:
 * - Expand common abbreviations
 * - Clean markdown artifacts
 * - Normalize whitespace and punctuation
 */
function preprocessForTTS(text) {
  return text
    // Strip markdown bold/italic markers
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
    .replace(/_{1,2}([^_]+)_{1,2}/g, '$1')
    // Strip markdown links — keep the label
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Strip code backticks
    .replace(/`([^`]+)`/g, '$1')
    // Expand common abbreviations
    .replace(/\betc\./gi, 'etcetera')
    .replace(/\be\.g\./gi, 'for example')
    .replace(/\bi\.e\./gi, 'that is')
    .replace(/\bvs\./gi, 'versus')
    .replace(/\bDr\./gi, 'Doctor')
    .replace(/\bMr\./gi, 'Mister')
    .replace(/\bMrs\./gi, 'Missus')
    .replace(/\bMs\./gi, 'Miss')
    .replace(/\bSt\./gi, 'Saint')
    // URLs — replace with "link" so TTS doesn't spell out the URL
    .replace(/https?:\/\/[^\s]+/g, 'link')
    // Em-dash and en-dash → comma pause
    .replace(/[—–]/g, ', ')
    // Ellipsis → period (natural pause)
    .replace(/\.{2,}/g, '.')
    // Bullet points / list markers
    .replace(/^[\s]*[-•*]\s+/gm, '')
    // Numbered list markers
    .replace(/^[\s]*\d+[.)]\s+/gm, '')
    // Collapse multiple spaces and trim
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Split text into natural sentences for individual TTS synthesis.
 * Handles abbreviations, decimals, and other edge cases to avoid
 * splitting mid-sentence.
 */
function splitIntoSentences(text) {
  // Split on sentence-ending punctuation followed by whitespace or end-of-string
  // Negative lookbehind avoids splitting on abbreviations (Mr., Dr., etc.)
  const raw = text.split(/(?<=[.!?])\s+/);
  const sentences = [];
  for (const chunk of raw) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;
    // If a sentence is very long (>200 chars), split on commas or semicolons
    if (trimmed.length > 200) {
      const subParts = trimmed.split(/(?<=[,;])\s+/);
      let current = '';
      for (const part of subParts) {
        if (current && (current + ' ' + part).length > 200) {
          sentences.push(current);
          current = part;
        } else {
          current = current ? current + ' ' + part : part;
        }
      }
      if (current) sentences.push(current);
    } else {
      sentences.push(trimmed);
    }
  }
  return sentences;
}

// Detect iOS — getUserMedia forces earpiece on iOS, so we use HTMLAudioElement for playback
const IS_IOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

export default function useKokoro(settings = {}) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [wordPulse, setWordPulse] = useState(false);

  const audioCtxRef = useRef(null);
  const abortRef = useRef(null);
  const onDoneRef = useRef(null);
  const pulseIntervalRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const audioElRef = useRef(null); // HTMLAudioElement for iOS playback
  const queueRef = useRef([]);
  const playingRef = useRef(false);

  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const connect = useCallback(async () => {
    console.log('[Kokoro] Ready (no connection needed)');
  }, []);

  /**
   * Synthesize a single sentence.
   * Returns an AudioBuffer (desktop) or ArrayBuffer of WAV data (iOS).
   */
  const synthesize = useCallback(async (sentence, signal) => {
    const res = await fetch(KOKORO_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: sentence,
        voice: 'bm_daniel',
        speed: 1.15,
      }),
      signal,
    });
    if (!res.ok) throw new Error('Kokoro ' + res.status);
    const wavBuffer = await res.arrayBuffer();
    // On iOS, return raw WAV to play through HTMLAudioElement (uses speaker route)
    if (IS_IOS) return wavBuffer;
    const ctx = ensureAudioContext();
    return ctx.decodeAudioData(wavBuffer);
  }, [ensureAudioContext]);

  /**
   * Play a queue of audio items sequentially.
   * On iOS: plays WAV ArrayBuffers through HTMLAudioElement (speaker route).
   * On desktop: plays AudioBuffers through AudioContext.
   */
  const playQueue = useCallback(async () => {
    if (playingRef.current) return;
    playingRef.current = true;

    while (queueRef.current.length > 0) {
      if (abortRef.current?.signal?.aborted) break;
      const buffer = queueRef.current.shift();

      if (IS_IOS) {
        // Play WAV via HTMLAudioElement — routes through speaker, not earpiece
        await new Promise((resolve) => {
          const blob = new Blob([buffer], { type: 'audio/wav' });
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audioElRef.current = audio;
          audio.onended = () => {
            audioElRef.current = null;
            URL.revokeObjectURL(url);
            resolve();
          };
          audio.onerror = () => {
            audioElRef.current = null;
            URL.revokeObjectURL(url);
            resolve();
          };
          audio.play().catch(() => resolve());
        });
      } else {
        const ctx = ensureAudioContext();
        await new Promise((resolve) => {
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          sourceNodeRef.current = source;
          source.onended = () => {
            sourceNodeRef.current = null;
            resolve();
          };
          source.start(0);
        });
      }
    }

    playingRef.current = false;
    clearInterval(pulseIntervalRef.current);
    setIsSpeaking(false);
    setWordPulse(false);
    onDoneRef.current?.();
  }, [ensureAudioContext]);

  const speak = useCallback(async (text) => {
    if (!text || !text.trim()) return;

    const cleaned = preprocessForTTS(text);
    const sentences = splitIntoSentences(cleaned);
    if (!sentences.length) return;

    setIsSpeaking(true);
    pulseIntervalRef.current = setInterval(() => {
      setWordPulse(p => !p);
    }, 150);

    const abortController = new AbortController();
    abortRef.current = abortController;
    queueRef.current = [];

    try {
      // Synthesize first sentence, start playing immediately, then prefetch rest
      const firstBuffer = await synthesize(sentences[0], abortController.signal);
      queueRef.current.push(firstBuffer);

      // Start playback of first sentence
      const playPromise = playQueue();

      // Synthesize remaining sentences in parallel with playback
      for (let i = 1; i < sentences.length; i++) {
        if (abortController.signal.aborted) break;
        const buf = await synthesize(sentences[i], abortController.signal);
        queueRef.current.push(buf);
      }

      await playPromise;
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[Kokoro] Error:', err);
      }
      clearInterval(pulseIntervalRef.current);
      setIsSpeaking(false);
      setWordPulse(false);
      sourceNodeRef.current = null;
      queueRef.current = [];
      onDoneRef.current?.();
    } finally {
      abortRef.current = null;
    }
  }, [synthesize, playQueue]);

  const stop = useCallback(() => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    if (sourceNodeRef.current) { try { sourceNodeRef.current.stop(); } catch {} sourceNodeRef.current = null; }
    if (audioElRef.current) { try { audioElRef.current.pause(); audioElRef.current.src = ''; } catch {} audioElRef.current = null; }
    if (pulseIntervalRef.current) { clearInterval(pulseIntervalRef.current); pulseIntervalRef.current = null; }
    queueRef.current = [];
    playingRef.current = false;
    setIsSpeaking(false);
    setWordPulse(false);
  }, []);

  const setVoiceId = useCallback(() => {}, []);
  const setTTSUrl = useCallback(() => {}, []);
  const disconnect = useCallback(() => { stop(); }, [stop]);
  const flush = useCallback(() => {}, []);

  return {
    isSpeaking, wordPulse, speak, flush, stop,
    connect, disconnect, setVoiceId, setTTSUrl, onDoneRef,
  };
}
